import { motion } from 'framer-motion'
import { Link } from 'react-router-dom'
import { usePageMeta } from '@/hooks/usePageMeta'

const WINE = '#3a1228'
const WINE_DEEP = '#1c0b14'
const WINE_SOFT = '#5c1a3a'
const CREAM = '#f4eee4'
const CREAM_SOFT = '#ebe3d6'
const INK_DARK = '#2c1a22'
const INK_MUTED = '#5a4450'
const LILAC = '#B9A3D9'
const GOLD = '#C8923A'
const PHOTO_SRC = 'https://static.lumi.new/66/66a59164029fe3389411bfef927c8644.webp'

const OFFERS = [
  {
    title: 'Cura Quântica Frequencial',
    text: 'Atendimento à distância que restaura sua conexão com a fonte primordial de energia vital, equilibrando e harmonizando seus níveis de vibração e frequência para a cura.',
    href: '/curaquantica',
    accent: WINE,
    mark: 'frequencia',
  },
  {
    title: 'Sessão de Liberação e Elevação Vibracional',
    text: 'Sessão pontual para liberar bloqueios emocionais e elevar sua vibração, trazendo mais leveza, clareza e movimento para sua vida.',
    href: '/sessao',
    accent: LILAC,
    mark: 'sessao',
  },
] as const

function OfferMark({ kind, color }: { kind: 'frequencia' | 'sessao'; color: string }) {
  if (kind === 'frequencia') {
    return (
      <svg width="44" height="44" viewBox="0 0 44 44" fill="none" aria-hidden="true">
        <circle cx="22" cy="22" r="18" stroke={color} strokeWidth="1.2" opacity="0.55" />
        <circle cx="22" cy="22" r="10" stroke={color} strokeWidth="0.8" opacity="0.35" />
        <path d="M8 22h28M22 8v28" stroke={color} strokeWidth="0.8" opacity="0.28" />
        <circle cx="22" cy="22" r="2.6" fill={color} opacity="0.7" />
      </svg>
    )
  }

  return (
    <svg width="44" height="44" viewBox="0 0 44 44" fill="none" aria-hidden="true">
      <circle cx="22" cy="22" r="18" stroke={color} strokeWidth="1.2" opacity="0.55" />
      <path d="M22 8 L22 36 M13 16 L31 28 M31 16 L13 28" stroke={color} strokeWidth="0.9" opacity="0.45" />
      <circle cx="22" cy="22" r="3" fill={color} opacity="0.65" />
    </svg>
  )
}

export default function PageAtendimentos() {
  usePageMeta({
    title: 'Escolha seu atendimento — Reacenda Luz',
    description: 'Selecione o tipo de atendimento que mais combina com o que você está vivendo agora.',
    ogImage: PHOTO_SRC,
    ogUrl: 'https://jornada.reacendaluz.com.br/atendimentos/',
  })

  return (
    <div className="min-h-screen w-full font-body" style={{ background: CREAM, color: INK_DARK }}>
      <header
        className="w-full px-4 py-5 flex items-center justify-center border-b"
        style={{
          background: WINE_DEEP,
          borderColor: 'rgba(255,255,255,0.08)',
        }}
      >
        <p className="text-[11px] tracking-[0.35em] uppercase font-body opacity-70" style={{ color: LILAC }}>
          Reacenda Luz · Mônica Maria
        </p>
      </header>

      <section
        className="relative overflow-hidden px-4 sm:px-6 py-16 md:py-24"
        style={{
          background: `linear-gradient(180deg, ${WINE_DEEP} 0%, ${WINE} 48%, ${WINE_SOFT} 100%)`,
        }}
      >
        <div
          className="pointer-events-none absolute -top-24 left-1/2 h-72 w-72 -translate-x-1/2 rounded-full"
          style={{ background: 'radial-gradient(closest-side, rgba(185,163,217,0.22), transparent 72%)' }}
        />
        <div className="relative mx-auto max-w-3xl text-center">
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.7 }}
            className="mb-5 text-[10px] tracking-[0.4em] uppercase font-body"
            style={{ color: LILAC }}
          >
            Atendimentos
          </motion.p>
          <motion.h1
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.1 }}
            className="font-display type-hero mb-6 text-white"
          >
            Escolha seu atendimento
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.22 }}
            className="mx-auto max-w-2xl font-body type-body leading-relaxed"
            style={{ color: 'rgba(255,255,255,0.82)' }}
          >
            Selecione o tipo de atendimento que mais combina com o que você está vivendo agora.
          </motion.p>
          <motion.div
            initial={{ width: 0, opacity: 0 }}
            animate={{ width: 80, opacity: 1 }}
            transition={{ duration: 1.1, delay: 0.4 }}
            className="h-px mx-auto mt-8"
            style={{ background: `linear-gradient(90deg, transparent, ${LILAC}, transparent)` }}
          />
        </div>
      </section>

      <section className="px-4 sm:px-6 py-14 md:py-20" style={{ background: CREAM }}>
        <div className="mx-auto max-w-5xl grid grid-cols-1 md:grid-cols-2 gap-5 md:gap-6">
          {OFFERS.map((offer, i) => (
            <motion.article
              key={offer.href}
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.15 + i * 0.1 }}
              className="flex h-full flex-col rounded-sm p-7 sm:p-8"
              style={{
                background: CREAM_SOFT,
                border: '1px solid rgba(58,18,40,0.08)',
                boxShadow: '0 14px 36px rgba(28,11,20,0.08)',
              }}
            >
              <div
                className="mb-6 h-1 w-12 rounded-full"
                style={{ background: offer.accent }}
              />
              <div className="mb-5">
                <OfferMark kind={offer.mark} color={offer.accent} />
              </div>
              <h2 className="font-display type-section mb-4" style={{ color: WINE }}>
                {offer.title}
              </h2>
              <p className="font-body type-body leading-relaxed mb-8 flex-1" style={{ color: INK_MUTED }}>
                {offer.text}
              </p>
              <Link
                to={offer.href}
                className="inline-flex min-h-[52px] w-full items-center justify-center rounded-lg px-6 py-3 font-body type-button tracking-[0.12em] uppercase transition-all duration-300 hover:brightness-110 active:scale-[0.98] focus-visible:outline-none"
                style={{
                  background: GOLD,
                  color: '#ffffff',
                  boxShadow: '0 8px 24px rgba(200,146,58,0.28)',
                }}
              >
                Saiba mais
              </Link>
            </motion.article>
          ))}
        </div>
      </section>

      <footer
        className="w-full px-4 py-10 text-center border-t"
        style={{ background: WINE_DEEP, borderColor: 'rgba(255,255,255,0.08)' }}
      >
        <p className="font-display italic text-[16px] mb-2" style={{ color: 'rgba(247,239,232,0.72)' }}>
          Com amor e presença, Mônica Maria
        </p>
        <p className="text-[11px] tracking-[0.3em] uppercase font-body opacity-50" style={{ color: LILAC }}>
          Reacenda Luz
        </p>
      </footer>
    </div>
  )
}
